"use client";
import _ from "lodash";
import { fCurrency, fCurrencyIDR, fCurrencyVND } from "@/utils/formatNumber";
import { ECurrency } from "@/models/general";
import { useAppSelector } from "@lib/hooks";
import { ReducerType } from "@lib/store";
import { useMemo } from "react";
import { TYPE_VOUCHER, Voucher2 } from "@/models/voucher";
import { Report } from "@/models/report";
import { subscription } from "@/models/subscription";
import { UserSubscription } from "@/models/subscriptionUser";
import moment from "moment";
import { ItemBuyMultipleSubscription } from "@/models/payment";
import { ceilingIDR, ceilingUSD } from "@/utils";

interface ICurrencyConvert {
  USD: number;
  VND: number;
  IDR: number;
  USDShow: string;
  VNDShow: string;
  IDRShow: string;
  show: string;
  equivalent: string;
}

interface SubscriptionUsage {
  isTrial: boolean;
  totalAmount: number;
  totalMonths: number;
  usedMonths: number;
  usedAmount: number;
  remainingAmount: number;
  remainingDays: number;
  progressPercentage: number;
  isExpired: boolean;
  // Có thể thêm các trường optional
  startDate?: string;
  endDate?: string;
}
const getCurrencyConvert = (
  costVND: number,
  currency: string,
  usdToVND: number | undefined,
  idrToVND: number | undefined
): ICurrencyConvert => {
  let show = "",
    equivalent = "";
  const VND = costVND;
  const USD = ceilingUSD(Number(costVND) / Number(usdToVND), 5);
  const IDR = ceilingIDR(Number(costVND) / Number(idrToVND), 5000);

  const USDShow = fCurrency(USD);
  const IDRShow = fCurrencyIDR(IDR);
  const VNDShow = fCurrencyVND(VND);
  switch (currency) {
    case ECurrency.USD:
      show = USDShow;
      equivalent = VNDShow;
      break;
    case ECurrency.VND:
      show = VNDShow;
      equivalent = USDShow;
      break;
    case ECurrency.IDR:
      show = IDRShow;
      equivalent = VNDShow;
      break;
  }

  return {
    USD: USD as any,
    VND: VND,
    IDR: IDR as any,
    USDShow: USDShow,
    VNDShow: VNDShow,
    IDRShow: IDRShow,
    show: show,
    equivalent: equivalent,
  };
};

export function usePrice(
  report?: Report | subscription,
  coupons?: Voucher2[],
  subscription?: UserSubscription | null,
  type?: string | null
) {
  // Use Redux instead of React Query to prevent "No QueryClient set" error
  const { user: userFromStore, configs: configsNew } = useAppSelector(
    (state: ReducerType) => state.auth
  );

  const currency = useMemo(() => {
    if (userFromStore) return userFromStore.currency;
    return ECurrency.VND;
  }, [userFromStore]);

  const amount = useMemo(() => {
    if (report) {
      return report?.amount;
    }
    return 0;
  }, [report]);

  const calculateUsage: SubscriptionUsage | null = useMemo(() => {
    if (!subscription) return null;
    const totalMonths = Number(subscription?.subscription?.month || 1);
    const totalAmount = subscription?.subscription.amount;
    const startDate = moment(
      subscription?.purchaseDate || subscription?.createdAt
    );
    const endDate = moment(startDate).add(totalMonths, "months");
    const now = moment();
    const totalDays = endDate.diff(startDate, "days");
    const usedDays =
      now.diff(startDate, "days") === 0 ? 1 : now.diff(startDate, "days");
    const remainingDays = endDate.diff(now, "days");
    const clampedUsedDays = Math.min(Math.max(0, usedDays), totalDays);
    const clampedRemainingDays = Math.max(0, remainingDays);
    const usedAmount = totalAmount * (clampedUsedDays / totalDays);
    const remainingAmount = totalAmount - usedAmount;
    return {
      isTrial: subscription?.isTrial,
      totalAmount: Math.round(totalAmount),
      totalMonths,
      usedMonths: parseFloat((clampedUsedDays / 30).toFixed(2)),
      usedAmount: Math.round(usedAmount),
      remainingAmount: Math.round(remainingAmount),
      remainingDays: clampedRemainingDays,
      progressPercentage: Math.round((clampedUsedDays / totalDays) * 100),
      isExpired: now.isAfter(endDate),
    };
  }, [subscription, subscription?.createdAt, subscription?.purchaseDate]);

  const usdToVND = useMemo(() => {
    return configsNew?.usdToVND;
  }, [configsNew]);

  const idrToVND = useMemo(() => {
    return configsNew?.idrToVND;
  }, [configsNew]);

  const vatConfig = useMemo(() => {
    return configsNew?.vat;
  }, [configsNew]);

  const preCostSubscription = useMemo(() => {
    if (calculateUsage?.isTrial) {
      return getCurrencyConvert(0, currency, usdToVND, idrToVND);
    }
    return getCurrencyConvert(
      calculateUsage?.remainingAmount ?? 0,
      currency,
      usdToVND,
      idrToVND
    );
  }, [
    calculateUsage?.isTrial,
    calculateUsage?.remainingAmount,
    currency,
    usdToVND,
    idrToVND,
  ]);

  const sampleSizeCost = useMemo(() => {
    return getCurrencyConvert(amount, currency, usdToVND, idrToVND);
  }, [amount, currency, usdToVND, idrToVND]);

  const totalAmountCostDefault = useMemo(() => {
    return getCurrencyConvert(
      amount < 0 ? 0 : amount,
      currency,
      usdToVND,
      idrToVND
    );
  }, [amount, currency, usdToVND, idrToVND]);

  const amountCostDefault = useMemo(() => {
    let costVND = sampleSizeCost?.VND ?? 0;
    if (
      type === "upgrade" &&
      subscription &&
      calculateUsage &&
      !calculateUsage?.isTrial
    ) {
      costVND = costVND - (calculateUsage?.remainingAmount ?? 0);
    }
    return getCurrencyConvert(costVND, currency, usdToVND, idrToVND);
  }, [
    sampleSizeCost?.VND,
    type,
    subscription,
    calculateUsage,
    currency,
    usdToVND,
    idrToVND,
  ]);

  const totalAmountVoucher = useMemo(() => {
    const _totalAmount = amountCostDefault?.VND ?? 0;
    let totalVoucher = 0;
    if (coupons?.length) {
      const tempTotalAmount = _.cloneDeep(_totalAmount);
      coupons.map((c: any) => {
        switch (c.typeAmount || c.type) {
          case TYPE_VOUCHER.FLAT:
            totalVoucher = totalVoucher + Number(c.amount);
            break;
          case TYPE_VOUCHER.PERCENTAGE:
            const percent = c?.percent ? c.percent : Number(c.amount) || 0;
            totalVoucher = totalVoucher + (tempTotalAmount * percent) / 100;
            break;
        }
      });
    }
    return getCurrencyConvert(totalVoucher, currency, usdToVND, idrToVND);
  }, [amountCostDefault?.VND, currency, usdToVND, idrToVND, coupons]);

  const amountCost = useMemo(() => {
    const amount =
      (amountCostDefault?.VND ?? 0) - (totalAmountVoucher?.VND ?? 0);
    return getCurrencyConvert(
      amount > 0 ? amount : 0,
      currency,
      usdToVND,
      idrToVND
    );
  }, [
    amountCostDefault?.VND,
    totalAmountVoucher?.VND,
    currency,
    usdToVND,
    idrToVND,
  ]);

  const vatCost = useMemo(() => {
    const costVND = (amountCost?.VND ?? 0) * Number(vatConfig);
    return getCurrencyConvert(costVND, currency, usdToVND, idrToVND);
  }, [amountCost?.VND, vatConfig, currency, usdToVND, idrToVND]);

  const totalAmountCost = useMemo(() => {
    const costVND = (amountCost?.VND ?? 0) + (vatCost?.VND ?? 0);
    return getCurrencyConvert(costVND, currency, usdToVND, idrToVND);
  }, [amountCost?.VND, vatCost?.VND, currency, usdToVND, idrToVND]);

  const getCostCurrency = (
    costVND: number,
    _currency?: string,
    _usdToVND?: number,
    _idrToVND?: number
  ) => {
    return getCurrencyConvert(
      costVND,
      _currency || currency,
      _usdToVND || usdToVND,
      _idrToVND || idrToVND
    );
  };

  const price: {
    sampleSizeCost: ICurrencyConvert;
    amountCost: ICurrencyConvert;
    vatCost: ICurrencyConvert;
    totalAmountCost: ICurrencyConvert;
    amountCostDefault: ICurrencyConvert;
    totalAmountCostDefault: ICurrencyConvert;
    calculateUsage: SubscriptionUsage | null;
    preCostSubscription: ICurrencyConvert;
  } = {
    sampleSizeCost,
    amountCost,
    vatCost,
    amountCostDefault: amountCostDefault,
    totalAmountCost,
    totalAmountCostDefault,
    calculateUsage,
    preCostSubscription,
  };

  return {
    price,
    getCostCurrency,
  };
}

export function usePriceMultipleSubscription(
  subscriptionBuys?: ItemBuyMultipleSubscription[],
  coupons?: any[]
) {
  const { user, configs: configsNew } = useAppSelector(
    (state: ReducerType) => state.auth
  );

  const currency = useMemo(() => {
    return user?.currency ?? ECurrency.VND;
  }, [user]);

  const amount = useMemo(() => {
    let amount: number = 0;
    if (subscriptionBuys?.length) {
      subscriptionBuys.forEach((item) => {
        amount += item?.subscription
          ? Number(item?.subscription.amount) * item.quantity
          : 0;
      });
    }
    return amount;
  }, [subscriptionBuys]);

  const usdToVND = useMemo(() => configsNew?.usdToVND, [configsNew]);
  const idrToVND = useMemo(() => configsNew?.idrToVND, [configsNew]);
  const vatConfig = useMemo(() => configsNew?.vat, [configsNew]);

  const sampleSizeCost = useMemo(() => {
    return getCurrencyConvert(amount, currency, usdToVND, idrToVND);
  }, [amount, currency, usdToVND, idrToVND]);

  const totalAmountCostDefault = useMemo(() => {
    return getCurrencyConvert(
      amount < 0 ? 0 : amount,
      currency,
      usdToVND,
      idrToVND
    );
  }, [currency, usdToVND, idrToVND]);

  const amountCostDefault = useMemo(() => {
    const costVND = sampleSizeCost?.VND ?? 0;
    return getCurrencyConvert(costVND, currency, usdToVND, idrToVND);
  }, [sampleSizeCost?.VND, currency, usdToVND, idrToVND]);

  const totalAmountVoucher = useMemo(() => {
    const _totalAmount = amountCostDefault?.VND ?? 0;
    let totalVoucher = 0;
    if (coupons?.length) {
      const tempTotalAmount = _.cloneDeep(_totalAmount);
      coupons.map((c: any) => {
        switch (c.typeAmount || c.type) {
          case TYPE_VOUCHER.FLAT:
            totalVoucher = totalVoucher + Number(c.amount);
            break;
          case TYPE_VOUCHER.PERCENTAGE:
            const percent = c?.percent ? c.percent : Number(c.amount) || 0;
            totalVoucher = totalVoucher + (tempTotalAmount * percent) / 100;
            break;
        }
      });
    }

    return getCurrencyConvert(totalVoucher, currency, usdToVND, idrToVND);
  }, [amountCostDefault?.VND, currency, usdToVND, idrToVND, coupons]);

  const amountCost = useMemo(() => {
    const amount =
      (amountCostDefault?.VND ?? 0) - (totalAmountVoucher?.VND ?? 0);

    return getCurrencyConvert(
      amount > 0 ? amount : 0,
      currency,
      usdToVND,
      idrToVND
    );
  }, [
    amountCostDefault?.VND,
    totalAmountVoucher?.VND,
    currency,
    usdToVND,
    idrToVND,
  ]);

  const vatCost = useMemo(() => {
    const costVND = (amountCost?.VND ?? 0) * Number(vatConfig);
    return getCurrencyConvert(costVND, currency, usdToVND, idrToVND);
  }, [amountCost?.VND, vatConfig, currency, usdToVND, idrToVND]);

  const totalAmountCost = useMemo(() => {
    const costVND = (amountCost?.VND ?? 0) + (vatCost?.VND ?? 0);
    return getCurrencyConvert(costVND, currency, usdToVND, idrToVND);
  }, [amountCost?.VND, vatCost?.VND, currency, usdToVND, idrToVND]);

  const getCostCurrency = (
    costVND: number,
    _currency?: string,
    _usdToVND?: number,
    _idrToVND?: number
  ) => {
    return getCurrencyConvert(
      costVND,
      _currency || currency,
      _usdToVND || usdToVND,
      _idrToVND || idrToVND
    );
  };

  const price: {
    sampleSizeCost: ICurrencyConvert;
    amountCost: ICurrencyConvert;
    amountCostDefault: ICurrencyConvert;
    vatCost: ICurrencyConvert;
    totalAmountCost: ICurrencyConvert;
    totalAmountCostDefault: ICurrencyConvert;
  } = {
    sampleSizeCost,
    amountCost,
    amountCostDefault: amountCostDefault,
    vatCost,
    totalAmountCost,
    totalAmountCostDefault,
  };

  return {
    price,
    getCostCurrency,
  };
}

export interface TotalPrice {
  sampleSizeCost: ICurrencyConvert;
  eyeTrackingSampleSizeCost: ICurrencyConvert;
  customQuestionCost: ICurrencyConvert;
  amountCost: ICurrencyConvert;
  vatCost: ICurrencyConvert;
  totalAmountCost: ICurrencyConvert;
  totalAmountCostDefault: ICurrencyConvert;
}
